import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import DashboardFooter from "./dashboard_footer";
import Canvas from "./dahboard_canvas";
import axios from "axios";
import Swal from "sweetalert2";
import localforage from "localforage";
import { useBaseUrl } from "../../utils/utils";
import { Link, useNavigate } from "react-router-dom";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s' };
const labelStyle: React.CSSProperties = { fontSize: '0.74rem', fontWeight: 700, display: 'block', marginBottom: 6, color: 'var(--uzima-shell-text)', textTransform: 'uppercase', letterSpacing: '0.3px' };

const STEPS = [
    { num: 1, label: 'Facility Details', icon: 'bx bx-building-house' },
    { num: 2, label: 'Description & Location', icon: 'bx bx-map' },
    { num: 3, label: 'Facility Admin', icon: 'bx bx-user-plus' },
    { num: 4, label: 'Review & Submit', icon: 'bx bx-check-circle' },
];

const AddClinic: React.FC = () => {
    const navigate = useNavigate();
    const base_url = useBaseUrl();
    const [step, setStep] = useState(1);

    // Step 1 – Facility basics
    const [facilityName, setFacilityName] = useState('');
    const [facilityEmail, setFacilityEmail] = useState('');
    const [facilityPhone, setFacilityPhone] = useState('');
    const [facilityWebsite, setFacilityWebsite] = useState('');
    const [postcode, setPostcode] = useState('');
    const [capacity, setCapacity] = useState('');
    const [logo, setLogo] = useState<File | null>(null);

    // Step 2 – Description & Location
    const [editorData, setEditorData] = useState('');
    const [counties, setCounties] = useState<any[]>([]);
    const [towns, setTowns] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCounty, setSelectedCounty] = useState('');
    const [selectedTown, setSelectedTown] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loadingCounties, setLoadingCounties] = useState(false);
    const [loadingTowns, setLoadingTowns] = useState(false);

    // Step 3 – Admin details
    const [adminFirstName, setAdminFirstName] = useState('');
    const [adminMiddleName, setAdminMiddleName] = useState('');
    const [adminLastName, setAdminLastName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPhone, setAdminPhone] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [adminConfirmPassword, setAdminConfirmPassword] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Add Facility - Uzima";
        fetchCounties();
        fetchCategories();
    }, []);

    const fetchCounties = async () => {
        setLoadingCounties(true);
        try {
            const r = await axios.get(`${base_url}counties`, { timeout: 10000 });
            if (r.data.status === 'success') setCounties(r.data.data || []);
            else setCounties([]);
        } catch { setCounties([]); }
        finally { setLoadingCounties(false); }
    };

    const fetchTowns = async (countyCode: string) => {
        setLoadingTowns(true);
        try {
            const r = await axios.post(`${base_url}towns`, { county_code: countyCode }, { timeout: 10000 });
            if (r.data.status === 'success') setTowns(r.data.data || []);
            else setTowns([]);
        } catch { setTowns([]); }
        finally { setLoadingTowns(false); }
    };

    const fetchCategories = async () => {
        try {
            const r = await axios.get(`${base_url}facilities/categories`, { timeout: 10000 });
            if (r.data.status === 'success') setCategories(r.data.data || []);
            else if (Array.isArray(r.data)) setCategories(r.data);
            else setCategories([]);
        } catch { setCategories([]); }
    };

    const handleCountyChange = (v: string) => {
        setSelectedCounty(v);
        setSelectedTown('');
        setTowns([]);
        if (v) fetchTowns(v);
    };

    const canProceed = (s: number) => {
        if (s === 1) return !!facilityName && !!facilityEmail && !!facilityPhone;
        if (s === 2) return !!selectedCounty && !!selectedTown && !!selectedCategory;
        if (s === 3) return !!adminFirstName && !!adminLastName && !!adminEmail && !!adminPassword && adminPassword === adminConfirmPassword;
        return true;
    };

    const goNext = () => { if (step < 4 && canProceed(step)) setStep(step + 1); };
    const goPrev = () => { if (step > 1) setStep(step - 1); };

    const getCsrfToken = async () => {
        try {
            const r = await axios.get(`${base_url}tok`, { timeout: 10000, withCredentials: true });
            return r.data?.message || '';
        } catch {
            return '';
        }
    };

    const handleSubmit = async () => {
        if (!canProceed(1) || !canProceed(2) || !canProceed(3)) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please complete all required fields.' });
            return;
        }
        if (adminPassword !== adminConfirmPassword) {
            Swal.fire({ icon: 'warning', title: 'Password Mismatch', text: 'Admin passwords do not match.' });
            return;
        }
        setIsSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('facility_name', facilityName);
            fd.append('facility_email', facilityEmail);
            fd.append('facility_phone', facilityPhone);
            fd.append('facility_website', facilityWebsite || 'www.website.org');
            fd.append('county_id', selectedCounty);
            fd.append('town_id', selectedTown);
            fd.append('category_id', selectedCategory);
            fd.append('capacity', capacity || '0');
            fd.append('postcode', postcode);
            fd.append('description', editorData);
            fd.append('admin_first_name', adminFirstName);
            fd.append('admin_middle_name', adminMiddleName);
            fd.append('admin_last_name', adminLastName);
            fd.append('admin_email', adminEmail);
            fd.append('admin_phone', adminPhone);
            fd.append('admin_password', adminPassword);
            if (logo) fd.append('logo', logo);

            const institutionId = await localforage.getItem<string | number>('institution_id');
            if (institutionId) fd.append('institution_id', String(institutionId));

            const csrfToken = await getCsrfToken();
            const token = await localforage.getItem<string>('token');
            const r = await axios.post(`${base_url}facilities/dashboard/facilities/add/`, fd, {
                timeout: 20000,
                withCredentials: true,
                headers: {
                    ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
                    ...(token ? { Authorization: `Token ${token}` } : {}),
                },
            });

            if (r.data.status === 'success') {
                await Swal.fire({ icon: 'success', title: 'Facility Created!', text: r.data.message || 'Facility has been created successfully with admin access.', confirmButtonColor: ACCENT });
                navigate('/institution/clinics');
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: r.data.message || 'Failed to create facility.' });
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Request failed. Please try again.';
            Swal.fire({ icon: 'error', title: 'Request Failed', text: msg });
        } finally { setIsSubmitting(false); }
    };

    const progressPct = ((step - 1) / (STEPS.length - 1)) * 100;

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Add Facility</h1>
                                <p>Register a new clinic or facility under your institution</p>
                            </div>
                            <Link to="/institution/clinics" className="sc-btn sc-btn-ghost" style={{ textDecoration: 'none' }}><i className="bx bx-arrow-back"></i> Back to Facilities</Link>
                        </div>

                        {/* Step Indicator */}
                        <div className="sc-card" style={{ marginBottom: 20 }}>
                            <div className="sc-card-body" style={{ padding: '20px 24px' }}>
                                {/* Progress bar */}
                                <div style={{ height: 3, borderRadius: 2, background: '#e5e7eb', marginBottom: 20 }}>
                                    <div style={{ height: '100%', borderRadius: 2, background: ACCENT, width: `${progressPct}%`, transition: 'width 0.3s' }}></div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    {STEPS.map(s => {
                                        const done = step > s.num;
                                        const active = step === s.num;
                                        return (
                                            <div key={s.num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: done ? 'pointer' : 'default', opacity: active || done ? 1 : 0.4 }}
                                                onClick={() => { if (done) setStep(s.num); }}>
                                                <div style={{ width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: done ? ACCENT : active ? ACCENT : '#e5e7eb', color: done || active ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: '0.82rem', transition: 'all 0.2s' }}>
                                                    {done ? <i className="bx bx-check" style={{ fontSize: '1.1rem' }}></i> : s.num}
                                                </div>
                                                <span style={{ fontSize: '0.7rem', fontWeight: active ? 700 : 500, color: active ? ACCENT : 'var(--uzima-shell-text-muted)' }}>{s.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Approval Notice */}
                        <div style={{ padding: '12px 18px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <i className="bx bx-shield-quarter" style={{ fontSize: '1.2rem', color: '#d97706' }}></i>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e' }}>Comprehensive Registration</div>
                                <div style={{ fontSize: '0.74rem', color: '#a16207' }}>This will create the facility, a facility admin account, and a billing wallet in one step.</div>
                            </div>
                        </div>

                        {/* ─── Step 1: Facility Details ─── */}
                        {step === 1 && (
                            <div className="sc-card">
                                <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Facility Details</div>
                                </div>
                                <div className="sc-card-body" style={{ padding: 24 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div>
                                            <label style={labelStyle}>Facility Name *</label>
                                            <input style={inputStyle} value={facilityName} onChange={e => setFacilityName(e.target.value)} placeholder="e.g. Nairobi Central Clinic" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Facility Email *</label>
                                            <input style={inputStyle} type="email" value={facilityEmail} onChange={e => setFacilityEmail(e.target.value)} placeholder="clinic@example.com" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Facility Phone *</label>
                                            <input style={inputStyle} type="tel" value={facilityPhone} onChange={e => setFacilityPhone(e.target.value)} placeholder="+254 7XX XXX XXX" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Website</label>
                                            <input style={inputStyle} type="url" value={facilityWebsite} onChange={e => setFacilityWebsite(e.target.value)} placeholder="https://..." />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Postcode</label>
                                            <input style={inputStyle} value={postcode} onChange={e => setPostcode(e.target.value)} placeholder="00100" />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Capacity (beds/seats)</label>
                                            <input style={inputStyle} type="number" min="0" value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="0" />
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <label style={labelStyle}>Logo</label>
                                            <input style={inputStyle} type="file" accept="image/png,image/jpeg" onChange={e => setLogo(e.target.files?.[0] || null)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ─── Step 2: Description & Location ─── */}
                        {step === 2 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-map me-2" style={{ color: ACCENT }}></i>Location & Category</div>
                                    </div>
                                    <div className="sc-card-body" style={{ padding: 24 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                                            <div>
                                                <label style={labelStyle}>County *</label>
                                                <select style={inputStyle} value={selectedCounty} onChange={e => handleCountyChange(e.target.value)} required>
                                                    <option value="">{loadingCounties ? 'Loading...' : 'Select County'}</option>
                                                    {counties.map(c => <option key={c.county_code} value={c.county_code}>{c.county}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Town *</label>
                                                <select style={{ ...inputStyle, opacity: (!selectedCounty || loadingTowns) ? 0.6 : 1 }} value={selectedTown} onChange={e => setSelectedTown(e.target.value)} disabled={!selectedCounty || loadingTowns} required>
                                                    <option value="">{loadingTowns ? 'Loading...' : selectedCounty ? 'Select Town' : 'Select county first'}</option>
                                                    {towns.map(t => <option key={t.town_id} value={t.town_id}>{t.town}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Category *</label>
                                                <select style={inputStyle} value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} required>
                                                    <option value="">Select Category</option>
                                                    {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div style={{ padding: '12px 18px', borderBottom: '2px solid #3b82f6', background: 'rgba(59,130,246,0.03)' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-edit me-2" style={{ color: '#3b82f6' }}></i>Description</div>
                                    </div>
                                    <div className="sc-card-body" style={{ padding: 24 }}>
                                        <CKEditor editor={ClassicEditor as any} data={editorData} onChange={(_: any, editor: any) => setEditorData(editor.getData())} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ─── Step 3: Facility Admin ─── */}
                        {step === 3 && (
                            <div className="sc-card">
                                <div style={{ padding: '12px 18px', borderBottom: '2px solid #8b5cf6', background: 'rgba(139,92,246,0.03)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-user-plus me-2" style={{ color: '#8b5cf6' }}></i>Facility Administrator</div>
                                </div>
                                <div className="sc-card-body" style={{ padding: 24 }}>
                                    <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 16 }}>
                                        This person will have full admin access to the new facility. They can manage staff, patients, and settings.
                                    </p>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                                        <div>
                                            <label style={labelStyle}>First Name *</label>
                                            <input style={inputStyle} value={adminFirstName} onChange={e => setAdminFirstName(e.target.value)} placeholder="John" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Middle Name</label>
                                            <input style={inputStyle} value={adminMiddleName} onChange={e => setAdminMiddleName(e.target.value)} placeholder="M." />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Last Name *</label>
                                            <input style={inputStyle} value={adminLastName} onChange={e => setAdminLastName(e.target.value)} placeholder="Doe" required />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                        <div>
                                            <label style={labelStyle}>Admin Email *</label>
                                            <input style={inputStyle} type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="admin@clinic.com" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Admin Phone</label>
                                            <input style={inputStyle} type="tel" value={adminPhone} onChange={e => setAdminPhone(e.target.value)} placeholder="+254 7XX XXX XXX" />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <div>
                                            <label style={labelStyle}>Password *</label>
                                            <input style={inputStyle} type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Strong password" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Confirm Password *</label>
                                            <input style={{ ...inputStyle, borderColor: adminConfirmPassword && adminPassword !== adminConfirmPassword ? '#ef4444' : undefined }} type="password" value={adminConfirmPassword} onChange={e => setAdminConfirmPassword(e.target.value)} placeholder="Re-enter password" required />
                                        </div>
                                    </div>
                                    {adminConfirmPassword && adminPassword !== adminConfirmPassword && (
                                        <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(244,63,94,0.08)', color: '#e11d48', fontSize: '0.78rem', fontWeight: 600, marginTop: 12 }}>
                                            <i className="bx bx-error-circle me-1"></i>Passwords do not match
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── Step 4: Review & Submit ─── */}
                        {step === 4 && (
                            <div className="sc-card">
                                <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-check-circle me-2" style={{ color: ACCENT }}></i>Review & Submit</div>
                                </div>
                                <div className="sc-card-body" style={{ padding: 24 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                        {/* Facility summary */}
                                        <div style={{ padding: 16, borderRadius: 10, background: 'rgba(15,23,42,0.02)', border: '1px solid var(--uzima-shell-border)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 12, color: ACCENT }}><i className="bx bx-building-house me-2"></i>Facility</div>
                                            {[
                                                ['Name', facilityName], ['Email', facilityEmail], ['Phone', facilityPhone],
                                                ['Website', facilityWebsite || '—'], ['Postcode', postcode || '—'], ['Capacity', capacity || '0'],
                                                ['County', counties.find(c => c.county_code === selectedCounty)?.county || selectedCounty],
                                                ['Town', towns.find(t => String(t.town_id) === selectedTown)?.town || selectedTown],
                                                ['Category', categories.find((c: any) => String(c.id) === selectedCategory)?.name || selectedCategory],
                                            ].map(([label, val], i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.78rem', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                                    <span style={{ color: 'var(--uzima-shell-text-muted)' }}>{label}</span>
                                                    <span style={{ fontWeight: 600 }}>{val}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Admin summary */}
                                        <div style={{ padding: 16, borderRadius: 10, background: 'rgba(15,23,42,0.02)', border: '1px solid var(--uzima-shell-border)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 12, color: '#8b5cf6' }}><i className="bx bx-user-plus me-2"></i>Facility Admin</div>
                                            {[
                                                ['Name', `${adminFirstName} ${adminMiddleName} ${adminLastName}`.trim()],
                                                ['Email', adminEmail], ['Phone', adminPhone || '—'],
                                                ['Password', '••••••••'],
                                            ].map(([label, val], i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.78rem', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                                    <span style={{ color: 'var(--uzima-shell-text-muted)' }}>{label}</span>
                                                    <span style={{ fontWeight: 600 }}>{val}</span>
                                                </div>
                                            ))}
                                            {editorData && (
                                                <div style={{ marginTop: 12 }}>
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)', fontWeight: 700, marginBottom: 4 }}>DESCRIPTION</div>
                                                    <div style={{ fontSize: '0.78rem', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: editorData }}></div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                            <button className="sc-btn sc-btn-ghost" onClick={goPrev} disabled={step === 1} style={{ opacity: step === 1 ? 0.4 : 1 }}>
                                <i className="bx bx-arrow-back me-1"></i> Previous
                            </button>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {step < 4 ? (
                                    <button className="sc-btn sc-btn-primary" onClick={goNext} disabled={!canProceed(step)}>
                                        Continue <i className="bx bx-arrow-right ms-1"></i>
                                    </button>
                                ) : (
                                    <button className="sc-btn sc-btn-primary" onClick={handleSubmit} disabled={isSubmitting || !canProceed(3)}>
                                        {isSubmitting ? <><i className="bx bx-loader-alt bx-spin me-1"></i> Creating...</> : <><i className="bx bx-check me-1"></i> Create Facility</>}
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default AddClinic;